import React from 'react';

import { Field, FieldProps, FieldRenderProps } from 'react-final-form';

import { MultipleRadioInput as Input } from '../../input';

/**
 * Компонент группы радиокнопок для множественного выбора.
 *
 * @param {Object} props - Свойства компонента.
 * @param {boolean} [props.disabled=false] - Флаг, указывающий, что компонент отключен.
 * @param {string} props.label - Метка группы радиокнопок.
 * @param {string} props.name - Имя поля формы.
 * @param {Array<Object>} props.options - Массив опций, где каждая опция содержит метку и идентификатор.
 * @param {string} props.trueLabel - Метка для значения 'always'.
 * @param {string} props.falseLabel - Метка для значения 'direct_only'.
 * @param {string} props.neverLabel - Метка для значения 'never'.
 * @param {Object} props.rest - Дополнительные свойства, передаваемые в Field.
 */
export const MultipleRadioInput = ({
  disabled = false,
  label,
  name,
  options,
  trueLabel,
  falseLabel,
  neverLabel,
  ...rest
}: {
  options: { label: string; id: string | number }[];
} & FieldProps<
  { id: string; value: 'always' | 'direct_only' | 'never' }[] | undefined,
  FieldRenderProps<
    { id: string; value: 'always' | 'direct_only' | 'never' }[] | undefined
  >
>) => {
  const [byHand, setByHand] = React.useState(false);

  return (
    <Field name={name} {...rest}>
      {({ input }) => {
        let groupValue: 'all' | 'none' | 'never' | 'hand' = 'hand';

        const values: {
          id: string | number;
          value: 'always' | 'direct_only' | 'never';
        }[] = options.map((option) => {
          const value =
            (input.value &&
              input.value.find((inputValue) => inputValue.id === option.id)) ||
            null;

          return value || { id: option.id, value: 'always' };
        });

        if (
          values.filter((opt) => opt.value === 'always').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'all';
        } else if (
          values.filter((opt) => opt.value === 'direct_only').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'none';
        } else if (
          values.filter((opt) => opt.value === 'never').length ===
            options.length &&
          !byHand
        ) {
          groupValue = 'never';
        }

        /**
         * Обработчик изменения состояния всех элементов группы.
         *
         * @param {string | number} optionValue - Новое значение для всех элементов.
         */
        const handleChangeForAllElements = (optionValue: string | number) => {
          if (optionValue === 'all') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'always' })),
            );
            setByHand(false);
          } else if (optionValue === 'none') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'direct_only' })),
            );
            setByHand(false);
          } else if (optionValue === 'never') {
            input.onChange(
              options.map((opt) => ({ id: opt.id, value: 'never' })),
            );
            setByHand(false);
          } else {
            setByHand(true);
          }
        };

        /**
         * Обработчик изменения состояния конкретного элемента группы.
         *
         * @param {string | number} id - Идентификатор элемента.
         * @returns {Function} Внутренняя функция обработчика изменения.
         */
        const handleChangeForElement =
          (id: string | number) => (optionValue: string | number) => {
            const currentValue = values || [];

            if (optionValue === 'always') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'always' },
              ]);
              setByHand(false);
            } else if (optionValue === 'direct_only') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'direct_only' },
              ]);
              setByHand(false);
            } else if (optionValue === 'never') {
              input.onChange([
                ...currentValue.filter((opt) => opt.id !== id),
                { id, value: 'never' },
              ]);
              setByHand(false);
            }
          };

        return (
          <Input
            {...input}
            falseLabel={falseLabel}
            neverLabel={neverLabel}
            disabled={disabled}
            groupValue={groupValue}
            label={label}
            onChangeForAllElements={handleChangeForAllElements}
            onChangeForElement={handleChangeForElement}
            options={options}
            trueLabel={trueLabel}
            values={values}
            name={name}
          />
        );
      }}
    </Field>
  );
};
